import React, { useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getRemovalJobStatus } from '../../../api/emailsApi/emails';
import { useAsyncInterval } from '../../utils/hooks';
import { SearchContainer } from '../searchContainer/SearchContainer';
import { SelectedRecordContainer } from '../selectedRecordContainer/SelectedRecordContainer';
import { DocAssistantCaptureContext } from '../docAssistant/DocAssistantCaptureContext';
import { useDocAssistantCapture } from '../docAssistant/useDocAssistantCapture';
import * as FolderActions from '../../actions/folders';
import * as MailActions from '../../actions/mail';
import * as RootActions from '../../actions/index';

import './SaveToContainer.scss';

export const SaveToContainer = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  const isSearchingRelatedRecords = useSelector((state) => state.mail.isSearchingRelatedRecords);
  const folderTemplates = useSelector((state) => state.mail.settings?.docrioSettings?.defaultFolderSettings || {});
  const removalJobId = useSelector((state) => state.mail.removalJobId);
  const isRemovingSaveLocation = useSelector((state) => state.mail.isRemovingSaveLocation);
  const emailId = useSelector((state) => state.mail.activeEmail);
  const folderMaps = useSelector((state) => state.folders.folderMaps);
  const isFetchingFolders = useSelector((state) => state.folders.isFetchingFolders);
  const isLoading = useSelector((state) => state.app.isLoading);
  const [removingSaveLocation, setRemovingSaveLocation] = useState(!!removalJobId.length);
  const [pollingRemoveJob, setPollingRemoveJob] = useState(false);

  // Mounted here (not in SearchContainer) so the Doc Assistant poll survives the search <-> documents
  // navigation a match triggers.
  const docAssistantCapture = useDocAssistantCapture();

  useAsyncInterval(async () => {
    if (!pollingRemoveJob) {
      await pollRemovalJob();
    }
  }, removingSaveLocation ? 2000 : null);

  const pollRemovalJob = async () => {
    if (removalJobId.length && isRemovingSaveLocation) {
      setPollingRemoveJob(true);
      try {
        // api call to get job status
        const result = await getRemovalJobStatus(emailId, removalJobId, ownerEmail);
        if (result.data?.jobStatus === 'failed') {
          console.error(`Error removing saved location:`, result.data);
          batch(() => {
            dispatch(MailActions.isRemovingSaveLocation(false));
            dispatch(RootActions.isLoading(false));
            dispatch(RootActions.changeAlertText('Attempt to remove the saved location failed. Please try again.', 'error'));
          });
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          location.reload();
        } else {
          console.error(`Error polling removal job`, e);
          batch(() => {
            dispatch(MailActions.setRemovalJobId(''));
            dispatch(RootActions.changeAlertText('Error polling saved location removal status. Reopen add-in.', 'error'));
          });
        }
      }
      setPollingRemoveJob(false);
    }
  };

  useEffect(() => {
    setRemovingSaveLocation(!!removalJobId.length);
    if (removalJobId.length) {
      dispatch(RootActions.changeAlertText('Removing Saved Location...'));
      dispatch(RootActions.isLoading(true));
      dispatch(MailActions.isRemovingSaveLocation(true));
    }
  }, [removalJobId]);

  // We only have folder templates if object is configured
  const isObjectConfigured = !!folderTemplates[Object.values(selectedRecords)?.[0]?.type];

  if (selectedRecords && isObjectConfigured && !Object.keys(folderMaps).length && !isFetchingFolders && isAuthenticated) {
    batch(() => {
      // Create folder map with home folder
      const folderMaps = {};
      for (const relatedToId of Object.keys(selectedRecords)) {
        folderMaps[relatedToId] = {
          '': {
            path: '',
            name: `${folderTemplates[selectedRecords[relatedToId].type].objectLabel} Home`,
            subfolders: [],
          },
        };
      }
      dispatch(FolderActions.setFolderMaps(folderMaps));
    });
  }
  return (
    <DocAssistantCaptureContext.Provider value={docAssistantCapture}>
      <div className={isLoading ? 'saveToContainer-deactivated' : ''}>
        { isSearchingRelatedRecords ?
          <SearchContainer
            showBack={!!Object.keys(selectedRecords).length}
          /> :
          <SelectedRecordContainer items={selectedRecords} isObjectConfigured={isObjectConfigured}/>
        }
      </div>
    </DocAssistantCaptureContext.Provider>
  );
};
